import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from './dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { KeyboardEvent, MouseEvent, ReactNode, SyntheticEvent } from 'react';
import { RiskAssessmentScores } from '../RiskAssessmentScores';
import { UpgradeTooltip } from '../UpgradeTooltip';

export interface ListCardAction {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  /** When set, the item renders disabled and hovering it shows `disabledReason` instead of running `onClick`. */
  disabled?: boolean;
  disabledReason?: string;
  /** Muted second line under the label (e.g. a file type). */
  description?: string;
  /** When set, the item opens a sub-menu with these actions instead of running `onClick`. */
  subActions?: ListCardAction[];
  /** Draws a divider above the item. */
  separatorBefore?: boolean;
}

/** One ⋯-menu entry -- a plain item, a sub-menu, or a disabled item with its reason tooltip. Shared by ListCard and GridCard. */
export function CardActionMenuItem({ action }: { action: ListCardAction }) {
  const content = action.description ? (
    <div className="flex flex-col">
      <span>{action.label}</span>
      <span className="text-xs text-muted-foreground">{action.description}</span>
    </div>
  ) : action.label;

  const item = action.subActions?.length && !action.disabled ? (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {action.icon}
        {content}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-[14rem]">
        {action.subActions.map((subAction, index) => (
          <CardActionMenuItem key={index} action={subAction} />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  ) : (
    <DropdownMenuItem
      onClick={action.disabled ? undefined : action.onClick}
      disabled={action.disabled}
      className={`${action.variant === 'danger' ? 'text-red-600' : ''} ${action.description ? 'items-start' : ''}`}
    >
      {action.icon}
      {content}
    </DropdownMenuItem>
  );

  return (
    <>
      {action.separatorBefore && <DropdownMenuSeparator />}
      {action.disabled ? (
        <UpgradeTooltip side="left" message={action.disabledReason}>{item}</UpgradeTooltip>
      ) : (
        item
      )}
    </>
  );
}

export interface ListCardBadge {
  label: string;
  variant?: 'default' | 'secondary' | 'outline';
  className?: string;
}

export interface ListCardMetric {
  label: string;
  value: string | number;
  icon?: ReactNode;
  className?: string;
}

export interface ListCardInfo {
  label: string;
  value: string;
  className?: string;
}

/**
 * Props that make a whole card open its main page: click or Enter runs `onOpen`;
 * Ctrl/Cmd+click or middle-click opens `href` in a new tab. Clicks on the card's
 * buttons, links and ⋯ menu, or that end a text selection, are ignored.
 * Shared by ListCard and GridCard.
 */
export function getClickableCardProps(label: string, onOpen?: () => void, href?: string) {
  if (!onOpen) return { className: '' };

  const isCardClick = (e: SyntheticEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    // ⋯ menu items render in a portal: React still bubbles their events here, but they sit outside the card's DOM.
    if (!e.currentTarget.contains(target)) return false;
    if (target.closest('button, a, input, textarea, select, [role="menu"], [role="menuitem"]')) return false;
    return !window.getSelection()?.toString();
  };
  const openInNewTab = () => {
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  return {
    role: 'link',
    tabIndex: 0,
    'aria-label': label,
    className: 'cursor-pointer hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    onClick: (e: MouseEvent<HTMLElement>) => {
      if (!isCardClick(e)) return;
      if ((e.ctrlKey || e.metaKey) && href) openInNewTab();
      else onOpen();
    },
    // Middle-click: block the browser's auto-scroll on press, open the new tab on release.
    onMouseDown: (e: MouseEvent<HTMLElement>) => {
      if (e.button === 1 && href && isCardClick(e)) e.preventDefault();
    },
    onAuxClick: (e: MouseEvent<HTMLElement>) => {
      if (e.button === 1 && isCardClick(e)) openInNewTab();
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' && e.target === e.currentTarget) onOpen();
    },
  };
}

export interface ListCardProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  badges?: ListCardBadge[];
  metrics?: ListCardMetric[];
  infoItems?: ListCardInfo[];
  vulnerabilities?: {
    critical?: number;
    high?: number;
    medium?: number;
    low?: number;
  };
  riskScores?: {
    overallScore?: number;
    vulnerabilityScore?: number;
    licenseScore?: number;
    operationalRisk?: number;
    technicalDebt?: number;
    licenseIssues?: number;
  };
  actions?: ListCardAction[];
  /** Inline controls rendered in the actions area, before the ⋯ menu. */
  actionButtons?: ReactNode;
  /** Makes the whole card clickable -- see getClickableCardProps. */
  onClick?: () => void;
  /** Page `onClick` opens; used for Ctrl/Cmd+click and middle-click to open it in a new tab. */
  href?: string;
  children?: ReactNode;
  className?: string;
}

export function ListCard({
  title,
  description,
  icon,
  badges = [],
  metrics = [],
  infoItems = [],
  vulnerabilities,
  riskScores,
  actions = [],
  actionButtons,
  onClick,
  href,
  children,
  className = ""
}: ListCardProps) {
  const { className: clickableClassName = '', ...clickableProps } = getClickableCardProps(title, onClick, href);

  return (
    <Card
      className={`rounded-lg shadow-sm border border-border hover:shadow-md transition-shadow ${clickableClassName} ${className}`}
      {...clickableProps}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            <div className="flex items-center space-x-3">
              {icon}
              <div className="min-w-0">
                <h3 className="font-semibold text-foreground truncate">
                  {title}
                </h3>
                {description && (
                  <p className="text-sm text-muted-foreground truncate">
                    {description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-6 grid grid-cols-1 gap-1">
              {badges.length > 0 && (
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {badges.map((badge, index) => (
                    <Badge key={index} className={badge.className} variant={badge.variant}>
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              )}
              {children}
            </div>
          </div>

          <div className="flex items-center space-x-6 grid grid-cols-1 gap-1">
            {metrics.length > 0 && (
              <div className="flex items-center justify-between w-full space-x-4 text-sm text-muted-foreground">
                {metrics.map((metric, index) => (
                  <div key={index} className={`flex items-center whitespace-nowrap ${metric.className || ''}`}>
                    {metric.icon}
                    <span>{metric.label}</span>
                  </div>
                ))}
              </div>
            )}

            {riskScores && (
              <div className="flex-shrink-0">
                <RiskAssessmentScores riskScores={riskScores} compact={true} />
              </div>
            )}

           
          </div>

          <div className="flex items-center space-x-4 flex-shrink-0">
            {actionButtons}

            {actions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {actions.map((action, index) => (
                    <CardActionMenuItem key={index} action={action} />
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
